
import {
  type Symbol, type OrderSide, type Quote, type SecurityType,
} from '../common';
import {type TradableSymbol} from '../defaults';

// Runtime market-data schemas preserve explicit null values from the backend.
// eslint-disable-next-line @typescript-eslint/no-restricted-types
type Nullable<T> = T | null;

/** One level inside a validated L2 composite book. */
export type L2CompositeLevel = {
  price?: Nullable<number>;
  quantity?: Nullable<number>;
  size?: Nullable<number>;
  time?: Nullable<string>;
  timeUtc?: Nullable<string>;
};

/** Validated composite order-book fields delivered by an L2 subscription. */
export type L2Composite = {
  asks?: Nullable<L2CompositeLevel[]>;
  bids?: Nullable<L2CompositeLevel[]>;
  layer_id?: Nullable<number | string>;
  max_visible_depth?: Nullable<number>;
  symbol?: Nullable<string>;
  timestamp_utc?: Nullable<string | Date>;
};

/** Schema-validated L2 subscription response exposed by the preload. */
export type L2DataSubscriptionResponse = {
  amount?: Nullable<string | number>;
  composite?: Nullable<L2Composite>;
  createdAt?: Nullable<string | Date>;
  description?: Nullable<string>;
  id?: Nullable<string | number>;
  meta?: Nullable<{
    createdAt?: Nullable<string | Date>;
    id?: Nullable<string | number>;
    isActive?: Nullable<boolean>;
    score?: Nullable<string | number>;
    updatedAt: string | Date;
  }>;
  name?: Nullable<string>;
  optionalField?: Nullable<string>;
  tags?: Nullable<string[]>;
  updatedAt?: Nullable<string | Date>;
};

export enum PricingResponseType {
  SNAPSHOT = 0,
  UPDATE = 1,
}

export type FlexSearchType = {
  text: string;
  secTypes?: string[];
  exchanges?: string[];
  limit?: number;
  tradeableSymbol?: TradableSymbol[];
};

/**
 * Parameters for `marketData.symbolLookup`. Replaces the previous 7 positional
 * arguments. Shared by frontend and webgateway via the @qep/types submodule.
 */
export type SymbolLookupRequest = {
  // Usually a search string, but several callers pass a full Symbol so the
  // webgateway SQL-fallback path can read fields like `underlyingRootSymbol`.
  symbol: string | Symbol;
  secTypes?: SecurityType[];
  exchanges?: string[];
  tradeable?: boolean;
  fallbackToSQL?: boolean;
  signal?: AbortSignal;
  limit?: number;
};

export const enum MarketDataSources {
  PROVIDER_ONETICK = 0,
  PROVIDER_QUANTHOUSE = 1,
}

/** Symbol identity accepted by historical time-series lookups. */
export type TimeSeriesLookupSymbol = {
  segmentMic?: string;
  symbol: string;
};

/** Compiler contract for the runtime-validated market-data history request. */
export type TimeSeriesLookupRequest = {
  endDate: string;
  extended?: Nullable<number>;
  interval: string;
  period?: Nullable<number>;
  signal?: AbortSignal;
  source: string;
  startDate: string;
  symbol: TimeSeriesLookupSymbol;
};

/** One historical price tick returned by the time-series service. */
export type TimeSeriesLookupTick = {
  close?: Nullable<number | string>;
  dt: string;
  high?: Nullable<number | string>;
  low?: Nullable<number | string>;
  open?: Nullable<number | string>;
  symbol: string;
  volume?: Nullable<number | string>;
};

/** Runtime-validated response returned by `marketdata.timeSeriesLookup`. */
export type TimeSeriesLookupResponse = {
  ticksList: TimeSeriesLookupTick[];
};

/**
 * @deprecated Use `TimeSeriesLookupRequest`; retained for legacy chart callers.
 */
export type TimeSeriesQuery = {
  symbol: Partial<Symbol>;
  startDate: string;
  endDate: string;
  interval: string;
  period: number;
  extended: boolean;
};

/**
 * @deprecated Use `TimeSeriesLookupResponse`; retained for legacy chart callers.
 */
export type TimeSeriesResponse = Record<string, unknown>;
// https://git.cglcloud.com/qep/proto_defs/blob/main/schema/qep/protobuf/market_data/internal/data_acquisition.proto#L76

/** Common product identity fields accepted by instrument-data subscriptions. */
type InstrumentDataSubscriptionSymbolBase = {
  exchangeSymbol?: Nullable<string>;
  segmentMic: string;
};

/** Minimal product identity accepted by instrument-data subscriptions. */
export type InstrumentDataSubscriptionSymbol = InstrumentDataSubscriptionSymbolBase & (
  | {qepSymbol: string; symbol?: Nullable<string>}
  | {qepSymbol?: Nullable<string>; symbol: string}
);

/** Legacy request identity accepted before a chart has resolved its segment MIC. */
export type InstrumentDataSubscriptionRequestSymbol = {
  exchangeSymbol?: Nullable<string>;
  segmentMic?: string;
} & (
  | {qepSymbol: string; symbol?: Nullable<string>}
  | {qepSymbol?: Nullable<string>; symbol: string}
);

/** Product identity guaranteed inside validated subscription responses. */
export type InstrumentDataSubscriptionSnapshotSymbol = InstrumentDataSubscriptionSymbol & {
  symbol: string;
};

/** Price fields present in one validated instrument-data snapshot value. */
export type InstrumentDataSubscriptionPrice = {
  dailyCumulativeVolume?: Nullable<number>;
  netChange?: Nullable<number>;
  pctChange?: Nullable<number>;
  price?: Nullable<number>;
  size?: Nullable<number>;
  timeUtc?: Nullable<string>;
};

/** Previous-settlement fields present in an instrument-data snapshot. */
export type InstrumentDataSubscriptionSettlement = {
  settlementPrice?: Nullable<number>;
  settlementStatus?: Nullable<string>;
  settlementTimeUtc?: Nullable<string>;
};

/** Calendar date fields used for open-interest metadata. */
export type InstrumentDataSubscriptionDate = {
  day?: Nullable<number>;
  month?: Nullable<number>;
  year?: Nullable<number>;
};

/** Pricing payload nested inside a validated instrument-data subscription. */
export type InstrumentDataSubscriptionSnapshot = {
  ask?: Nullable<InstrumentDataSubscriptionPrice>;
  bid?: Nullable<InstrumentDataSubscriptionPrice>;
  close?: Nullable<number>;
  high?: Nullable<number>;
  lastTrade?: Nullable<InstrumentDataSubscriptionPrice>;
  limitHighPrice?: Nullable<number>;
  limitLowPrice?: Nullable<number>;
  low?: Nullable<number>;
  open?: Nullable<number>;
  openInterest?: Nullable<number>;
  openInterestDate: InstrumentDataSubscriptionDate;
  openInterestTimeUtc?: Nullable<string>;
  openingPriceUtc?: Nullable<string>;
  previousClose?: Nullable<number>;
  previousCloseExecutionTimeUtc?: Nullable<string>;
  previousDailySettlement?: Nullable<InstrumentDataSubscriptionSettlement>;
  previousHigh?: Nullable<number>;
  previousLow?: Nullable<number>;
  previousOpen?: Nullable<number>;
  previousOpenExecutionTimeUtc?: Nullable<string>;
  previousOpenInterest?: Nullable<number>;
  symbol?: Nullable<InstrumentDataSubscriptionSnapshotSymbol>;
};

/**
 * Runtime-validated L1 subscription envelope exposed by
 * `marketdata.subscribeToInstrumentData`.
 */
export type InstrumentDataSubscriptionResponse = {
  provider: string;
  snapshot: {
    snapshot: InstrumentDataSubscriptionSnapshot;
    symbol: InstrumentDataSubscriptionSnapshotSymbol;
  };
  update?: FuturesUpdate;
};

export type FutureSubscriptionResponse = {
  provider: string;
  update?: FuturesUpdate;
  snapshot?: FuturesSnapshot;

};
export type SubscribeFuturesResponse = {
  symbol: Symbol;
  subscriptionUID: string;

};

export type FuturesSnapshotPricing = {
  high?: number;
  low?: number;
  lastTrade?: LastTrade;
  bid?: Quote;
  ask?: Quote;
  dailySettlment: SettlementInfo;
  previousDailySettlement: SettlementInfo;
  limitHighPrice: number;
  limitLowPrie: number;
  openInterest: string;
  openInterestDate: OpenInterestDate;
  openingPriceUtc: Date;
  previousOpenInterest: string;
};

export type SettlementInfo = {
  settlementPrice: number;
  settlementTimeUtc: Date;
  settlementStatus: string;
};

export type OpenInterestDate = {
  day: number;
  month: number;
  year: number;
};

export type FuturesUpdate = {
  symbol: Symbol;
  level1Event: Level1Event;// Add this later @todo
  timestampUtc: string;
  feedTimestampUtc: string;
};
export type Level1Event = {
  bid?: Quote;
  ask?: Quote;
  lastTrade?: LastTrade;
  mid?: number;
  high?: number;
  low?: number;
  traded?: number;
  open_interest?: number;
  previous_close?: number;
  expiry?: Date;
  open_time?: Date;
  close_time?: Date;
};

export type LastTrade = {
  dailyCumulativeVolume: number;
  netChange: number;
  pctChange: number;
  price: number | string;
  size: number;
  timeUtc: Date;
};

// dailyCumlativeVolume

export type FuturesSnapshot = {
  symbol: Symbol;
  isFrontMonth?: boolean;
  high?: number;
  low?: number;
  mid?: number;
  last?: Quote;
  bid?: Quote;
  ask?: Quote;
  previous_net_change?: number;
};

export type BasketValueRequest = {
  symbols: BasketSymbol[];

};
export enum BasketSide {
  BUYSIDE = 1,
  SELLSIDE = 2,
}
export type BasketSymbol = {
  symbol: Symbol;
  side: OrderSide;
  quantity: number;
};
export type BasketValue = {
  grossNotionalValue: number;
  netNotionalValue: number;
};
export type TimeSeriesSymbol = {
  symbol: string;
  DT: string;
  Open: string;
  High: string;
  Low: string;
  Close: string;
  Volume: string;
  provider: string;
};

export enum CurrencyTypes {
  cents = 'USX',
  dollars = 'USD',
}

export type InstrumentDataUpdate = {
  provider: string;
  snapshot: {
    snapshot: FuturesSnapshotPricing & Record<string, unknown>;
  };
  symbol: Symbol;
};
